// Pure stats derivation for the Invoices page. Every filter (search,
// entity, reconciliation status, taxonomy, ...) is applied server-side now
// (backend/services/invoice_query.py's build_invoice_query +
// backend/services/recon_state.py's resolve_recon_status_filter) -- what
// GET /invoices returns for the current page/filters IS the filtered set,
// so there is no client-side filterInvoices() here anymore. Stats below
// still derive from that same array the table renders, so they cannot
// structurally diverge from it (the pre-existing bug this module's stats
// functions were extracted to fix).

export function sumByCurrency(arr) {
  const result = {};
  for (const inv of arr) {
    const cur = inv.currency || 'GBP';
    result[cur] = (result[cur] || 0) + (inv.amount || 0);
  }
  return result;
}

// Single shared overdue predicate -- used by both the per-row "(overdue)"
// badge and computeInvoiceStats' Overdue headline card, so they cannot
// structurally diverge. Mirrors routes/dashboard.py get_dashboard_summary's
// overdue query (payment_status != "paid" AND due_date < now); if either
// side's definition of "overdue" changes, check the other.
export function isOverdue(invoice) {
  if (invoice.payment_status === 'paid' || !invoice.due_date) return false;
  const d = new Date(invoice.due_date);
  return !isNaN(d.getTime()) && d < new Date();
}

// Mirrors backend/config.py's EXCLUDED_REVIEW_STATES exactly -- the single
// canonical list every backend invoice consumer (ledger/P&L aggregates,
// matching, accounting sync) gates on. This page's own stat-card totals
// used to diverge from it: a pending_review doc (correctly excluded from
// every backend aggregate) was NOT excluded here, so a doc whose stored
// amount was silently wrong (e.g. a historical pre-81175109 zero) folded
// straight into "Total"/"Unpaid" with no signal -- the exclusion-audit
// drift class (CLAUDE.md section 7 item 4) crossing the API boundary.
// See excludedReviewStatesFrontendAlignment.test.js's drift guard: if the
// backend list ever changes, this constant must change with it or that
// test fails.
// "outbound_correspondence" (2026-08-25 prompt "zero-amount invoices"): an
// own-domain-sender, document_type=="invoice", amount-missing, no-PDF doc
// -- the operator's own outbound correspondence, not a real invoice. See
// backend/services/outbound_correspondence.py.
export const EXCLUDED_REVIEW_STATES = ['pending_review', 'quarantined', 'rejected', 'merged_duplicate', 'outbound_correspondence'];

// A row not yet safe to fold into this page's own reliable stat-card
// totals: a review_status the backend treats as not-yet-trustworthy
// (mirrors EXCLUDED_REVIEW_STATES above), or amount_parse_suspect
// (services/non_invoice_cleanup.py's informational flag for a quarantined
// doc that looks invoice-shaped but whose amount still failed to parse).
// Excluded from the stat sums/counts -- surfaced instead as an explicit
// "(N excluded)" footnote, same convention AssetsLiabilitiesSection.jsx
// uses for unparseable_excluded_count.
export function isExcludedFromReliableStats(invoice) {
  return EXCLUDED_REVIEW_STATES.includes(invoice.review_status) || !!invoice.amount_parse_suspect;
}

// A quarantined/pending_review/rejected/amount-parse-suspect row must never
// render as a plain Unpaid invoice -- the returned badge is additional to
// the payment_status badge, never a substitute. amount_parse_suspect takes
// priority over the bare review_status label since it names the specific
// problem (an unparseable amount on an otherwise invoice-shaped doc)
// rather than the generic quarantine state. Returns null for a row with no
// review-status story to tell (the common "approved" case).
export function reviewStatusBadge(invoice) {
  if (invoice.amount_parse_suspect) {
    return { label: 'Unparseable amount', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
  }
  if (invoice.review_status === 'quarantined') {
    return { label: 'Quarantined', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
  }
  if (invoice.review_status === 'pending_review') {
    return { label: 'Pending review', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' };
  }
  if (invoice.review_status === 'rejected') {
    if (invoice.auto_rejected_by_classification) {
      return { label: 'Rejected (auto, not financial)', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' };
    }
    return { label: 'Rejected', className: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
  }
  if (invoice.review_status === 'outbound_correspondence') {
    return { label: 'Outbound correspondence', className: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
  }
  return null;
}

// Additive, never a substitute for reviewStatusBadge above -- a row can be
// both a normal approved invoice AND probably a duplicate notification
// (the field is stamped informationally, never auto-excluding). Covers
// both the Phase B parse-failure guard and the Commit 3 both-parsed-
// successfully guard, since they share the same field.
export function duplicateNotificationBadge(invoice) {
  if (!invoice.probable_duplicate_notification) return null;
  return { label: 'Possible duplicate', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
}

export function computeInvoiceStats(filteredInvoices, isOverdue) {
  const reliable = filteredInvoices.filter((i) => !isExcludedFromReliableStats(i));
  const excludedCount = filteredInvoices.length - reliable.length;

  const unpaidInvoices = reliable.filter((i) => i.payment_status === 'unpaid');
  const overdueInvoices = reliable.filter(isOverdue);
  const paidInvoices = reliable.filter((i) => i.payment_status === 'paid');

  return {
    total: {
      count: reliable.length, amounts: sumByCurrency(reliable),
      excludedCount,
    },
    unpaid: { count: unpaidInvoices.length, amounts: sumByCurrency(unpaidInvoices) },
    overdue: { count: overdueInvoices.length, amounts: sumByCurrency(overdueInvoices) },
    paid: { count: paidInvoices.length, amounts: sumByCurrency(paidInvoices) },
  };
}
